import { css } from "@emotion/css";
import { Badge, Callout, InlineMessage, SectionHeader } from "@octopusdeploy/design-system-components";
import { BanIcon, CheckCircleIcon, UnavailableIcon, XmarkCircleIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import type { MachineResource } from "@octopusdeploy/octopus-server-client";
import { EndpointsHelper, isDeploymentTarget, KubernetesMonitorStatus, UpgradeStatus } from "@octopusdeploy/octopus-server-client";
import { useAnalyticKubernetesAgentSetupDispatcher, useCorrelationId } from "~/analytics/Analytics";
import { OctopusPermissionsControllerInstallDialog } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/OctopusPermissionsController/OctopusPermissionsControllerInstallDialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { FormSection } from "~/components/form";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
interface BundledComponentsProps {
    machine: MachineResource;
    isRunningInHACluster: boolean;
}
const styles = {
    haWarning: css({
        marginTop: space[8],
    }),
    versionContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
    }),
};
function getKubernetesMonitorStatusBadge(monitorEnabled: boolean, status: KubernetesMonitorStatus) {
    if (!monitorEnabled) {
        return <Badge size="medium" variant="neutral" label="Inactive" icon={<UnavailableIcon size={20}/>}/>;
    }
    switch (status) {
        case KubernetesMonitorStatus.Online:
            return <Badge size="medium" variant="success" label="Online" icon={<CheckCircleIcon size={20}/>}/>;
        case KubernetesMonitorStatus.Offline:
            return <Badge size="medium" variant="danger" label="Offline" icon={<XmarkCircleIcon size={20}/>}/>;
        case KubernetesMonitorStatus.NotRegistered:
            return <Badge size="medium" variant="warning" label="Not Registered" icon={<BanIcon size={20}/>}/>;
    }
}
function getPermissionsControllerStatusBadge(version: string | null | undefined) {
    if (version) {
        return <Badge size="medium" variant="success" label="Online" icon={<CheckCircleIcon size={20}/>}/>;
    }
    else {
        return <Badge size="medium" variant="neutral" label="Inactive" icon={<UnavailableIcon size={20}/>}/>;
    }
}
export function BundledComponents({ machine, isRunningInHACluster }: BundledComponentsProps) {
    const octopusPermissionsControllerFeatureToggleEnabled = useOctopusFeatureToggle("octopus-permissions-controller", false);
    const isGrpcHaEnabled = useOctopusFeatureToggle("grpc-high-availability", false);
    const { isOpen: isPermissionsDialogOpen, openDialog: openPermissionsDialog, closeDialog: closePermissionsDialog } = useDialogTrigger();
    const correlationId = useCorrelationId();
    const analyticsDispatcher = useAnalyticKubernetesAgentSetupDispatcher(correlationId, "Machine Settings");
    if (!EndpointsHelper.isKubernetesTentacle(machine.Endpoint)) {
        return null;
    }
    const agentDetails = machine.Endpoint.KubernetesAgentDetails;
    if (!agentDetails || !isDeploymentTarget(machine)) {
        return null;
    }
    const disabledBecauseHaIsNotSupported = isRunningInHACluster && !isGrpcHaEnabled;
    const monitorEnabled = agentDetails.KubernetesMonitorEnabled;
    const monitorStatusBadge = getKubernetesMonitorStatusBadge(monitorEnabled, agentDetails.KubernetesMonitorStatus);
    const permissionsControllerStatusBadge = getPermissionsControllerStatusBadge(agentDetails.OctopusPermissionsControllerVersion);
    const permissionsControllerVersion = agentDetails.OctopusPermissionsControllerVersion;
    const permissionsControllerUpgradeAction = {
        label: "Upgrade now",
        onClick: () => openPermissionsDialog(),
    };
    const permissionsControllerVersionMessage = agentDetails.PermissionsControllerUpgradeStatus === UpgradeStatus.UpgradeAvailable ? <InlineMessage type={"information"} title={"New version available"} description={""} action={permissionsControllerUpgradeAction}/> : null;
    const permissionsControllerVersionDisplay = (<span className={styles.versionContainer}>
            {permissionsControllerVersion}
            {permissionsControllerVersionMessage}
        </span>);
    return (<>
            <SectionHeader title="Bundled Components"/>
            <section>
                <FormSection title="Kubernetes Monitor Status" help={monitorStatusBadge} includeBorder={true}/>
                {disabledBecauseHaIsNotSupported && (<div className={styles.haWarning}>
                        <Callout type="warning" title="Kubernetes Monitor is not supported in HA clusters"/>
                    </div>)}
                {octopusPermissionsControllerFeatureToggleEnabled && (<>
                        <FormSection title="Permissions Controller Status" help={permissionsControllerStatusBadge} includeBorder={true}/>
                        {permissionsControllerVersion && <FormSection title="Permissions Controller Version" help={permissionsControllerVersionDisplay} includeBorder={true}/>}
                        <OctopusPermissionsControllerInstallDialog machine={machine} open={isPermissionsDialogOpen} closeDialog={closePermissionsDialog} isUpgrading={true} dispatchAction={analyticsDispatcher}/>
                    </>)}
            </section>
        </>);
}
